import { get, map } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { getFormSyncErrors, submit, reset } from 'redux-form';
import { injectIntl } from 'react-intl';
import { hideDialog, showDialog } from '../../../core/actions';
import { checkErrors, checkVideoUrl } from '../../../core/formValidation';
import { getApiStatus, toString } from '../../../core/utils';
import {
  selectSiteModelYears,
  selectSiteOperationTypes,
  selectSiteTotalCarCount,
} from '../../settings/reducer';
import { selectEmployees } from '../../employees/reducer';
import { generateToastr } from '../../../core/toastMessages';
import TaskEditForm from '../components/TaskEditForm';
import { dependencies, Interval, TaskType } from '../models';
import { TaskCompletionStatus } from '../types';
import {
  selectTaskInEdit,
  selectTaskLocations,
  selectTaskPrefixes,
  selectTasks,
  selectMaintenanceTaskInEdit,
  selectCurrentEmployee,
} from '../reducer';
import { fetchTasks, getTaskLocations, getTaskPrefixes } from '../actions';
import TaskModalDialog from '../../../core/components/TaskModal';
import { getSiteModelYears, getSiteOperationTypes } from '../../settings/actions';
import { selectRole, selectSite } from '../../../store/authReducer';
import { RoleTypes } from '../../../core/components/Permission';
import {
  completeTaskAOT,
  revertTaskCompletion,
  snoozeTaskCompletion,
  unsnoozeTaskCompletion,
} from '../../overview/actions';

const initialFormValues = {
  name: '',
  subTitle: '',
  description: null,
  locationId: '',
  start: moment(),
  weeklySchedule: moment().day().toString(),
  monthlySameWeekday: false,
  alertIfOverdue: true,
  type: TaskType.Maintenance,
  interval: Interval.CarCount,
  intervalCount: 1,
  hourlySchedule: [],
  prefixId: 0,
  carCount: 0,
  linkedTasks: [],
  projectStatus: 'Backlog',
  priority: 'Low',
};

class UpsertTaskDialog extends Component {
  static propTypes = {
    onSubmit: PropTypes.func.isRequired,
    triggerSubmit: PropTypes.func.isRequired,
    triggerReset: PropTypes.func.isRequired,
    isOpen: PropTypes.bool.isRequired,
    closeDialog: PropTypes.func.isRequired,
    initialTask: PropTypes.shape({
      startingCarCount: PropTypes.number,
      carCount: PropTypes.number,
    }),
    isNew: PropTypes.bool,
    completeQuickAddTask: PropTypes.bool,
    isEssentialTask: PropTypes.bool,
    title: PropTypes.string.isRequired,
    errors: PropTypes.shape({}),
    location: PropTypes.shape({}),
    totalCarCount: PropTypes.number,
    fetchTaskPrefixes: PropTypes.func,
    fetchOperationTypes: PropTypes.func,
    fetchSiteModelYears: PropTypes.func,
    taskPrefixList: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.number,
        message: PropTypes.string,
      })
    ),
    taskLocationList: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.number,
        message: PropTypes.string,
        orderNumber: PropTypes.number,
      })
    ),
    employees: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
      })
    ),
    siteOperationTypeList: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      })
    ),
    siteModelYearList: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        year: PropTypes.string,
      })
    ),
    modelYears: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        year: PropTypes.string,
      })
    ),
    taskDefaults: PropTypes.shape({}),
    intl: PropTypes.shape({}).isRequired,
    showDialog: PropTypes.func,
    completeTaskAOT: PropTypes.func,
    revertTaskCompletion: PropTypes.func,
    snoozeTaskCompletion: PropTypes.func,
    unsnoozeTaskCompletion: PropTypes.func,
    site: PropTypes.number,
    currentEmployee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isSubmitting: PropTypes.bool,
  };

  state = {
    selectedTasks: [],
    selectedDependencies: [],
  };

  formRef = null;

  handleTaskSelect = (selectedTasks) => {
    this.setState({ selectedTasks });
  };

  handleDependencySelect = (selectedDependencies) => {
    this.setState({ selectedDependencies });
  };

  statusDisplayForProject = (task) => {
    if (!task) return '';
    const latestStatus = task?.latestCompletion?.status;
    if (latestStatus === undefined || latestStatus === null) return 'To Do';
    const statusEnum = toString(TaskCompletionStatus, latestStatus);
    const lower = (statusEnum || '').toLowerCase();
    if (lower === 'snoozed') return 'In Progress';
    if (lower === 'completed') return 'Completed';
    return 'To Do';
  };

  getProjectStatus = (task) => {
    if (!task || task.type !== TaskType.Project) return 'To Do';
    return this.statusDisplayForProject(task);
  };

  getLatestCompletionId = (task) => {
    const completion = task?.latestCompletion;
    if (!completion) return undefined;
    return (
      completion.id ??
      completion._id ??
      completion.taskCompletionId ??
      completion.task_completion_id ??
      completion.completionId ??
      completion.uuid ??
      completion.guid
    );
  };

  getTaskCompletionText = (task) => {
    if (!task) return '';
    return task.interval !== Interval.CarCount
      ? this.props.intl.formatMessage({ id: 'willSkipNextTime' })
      : this.props.intl.formatMessage({ id: 'willUpdateCarCountTask' });
  };

  autoStartInProgress = (taskId) => {
    const { currentEmployee } = this.props;
    const task = this.props.tasks.find((t) => t.id === taskId);
    if (!task || !currentEmployee) return;
    try {
      this.props.completeTaskAOT({
        taskId: task.id,
        employeeId: currentEmployee,
        notes: null,
      });
    } catch (e) {}
    this.awaitCompletionAndSnooze(taskId, 0);
  };

  awaitCompletionAndSnooze = (taskId, tries = 0) => {
    const maxTries = 10;
    if (tries > maxTries) {
      generateToastr(
        'error',
        this.props.intl.formatMessage({
          id: 'unableToChangeStatus',
          defaultMessage: 'Unable to change status',
        }),
        this.props.intl.formatMessage({
          id: 'noActiveCompletionFound',
          defaultMessage:
            'No active task completion found for today. Open the task to manage status.',
        })
      );
      return;
    }
    setTimeout(() => {
      try {
        this.props.getTasks();
      } catch (e) {}
      const refreshed = this.props.tasks.find((t) => t.id === taskId);
      const latestId = this.getLatestCompletionId(refreshed);
      if (latestId) {
        const employeeId = this.props.currentEmployee;
        const completionIds = [latestId];
        this.props.snoozeTaskCompletion({
          employeeId,
          taskIds: completionIds,
          notes: null,
          until: moment().endOf('day').toISOString(),
          options: { siteId: this.props.site },
          callback: () => this.props.getTasks(),
        });
      } else {
        this.awaitCompletionAndSnooze(taskId, tries + 1);
      }
    }, 700);
  };

  applyProjectStatusChange = (nextLabel) => {
    const task = this.props.initialTask;
    if (!task || task.type !== TaskType.Project) return;
    const current = this.getProjectStatus(task);
    if (!nextLabel || nextLabel === current) return;
    const latestId = this.getLatestCompletionId(task);
    const employeeId = this.props.currentEmployee;
    const completionIds = latestId ? [latestId] : [];
    const common = {
      options: { siteId: this.props.site },
      callback: () => this.props.getTasks(),
    };

    if (nextLabel === 'Completed') {
      const dialogProps = {
        onReady: this.props.completeTaskAOT,
        task,
        text: this.getTaskCompletionText(task),
        title: this.props.intl.formatMessage({ id: 'completeTaskAOT' }),
        isValid: true,
      };
      this.props.showDialog('COMPLETE_TASK_AOT', dialogProps);
      return;
    }

    if (!employeeId) {
      generateToastr(
        'error',
        this.props.intl.formatMessage({ id: 'unableToChangeStatus' }),
        this.props.intl.formatMessage({
          id: 'noActiveCompletionFound',
          defaultMessage:
            'No active task completion found for today. Open the task to manage status.',
        })
      );
      return;
    }

    if (nextLabel === 'To Do') {
      if (current === 'Completed' && latestId) {
        this.props.revertTaskCompletion({
          employeeId,
          taskIds: completionIds,
          notes: null,
          ...common,
        });
      } else if (current === 'In Progress' && latestId) {
        this.props.unsnoozeTaskCompletion({
          employeeId,
          taskIds: completionIds,
          notes: null,
          ...common,
        });
      } else if (!latestId) {
        generateToastr(
          'error',
          this.props.intl.formatMessage({ id: 'unableToChangeStatus' }),
          this.props.intl.formatMessage({
            id: 'noActiveCompletionFound',
            defaultMessage:
              'No active task completion found for today. Open the task to manage status.',
          })
        );
      }
      return;
    }

    if (nextLabel === 'In Progress') {
      if (!latestId) {
        this.autoStartInProgress(task.id);
        return;
      }
      const snooze = () =>
        this.props.snoozeTaskCompletion({
          employeeId,
          taskIds: completionIds,
          notes: null,
          until: moment().endOf('day').toISOString(),
          ...common,
        });
      if (current === 'Completed') {
        this.props.revertTaskCompletion({
          employeeId,
          taskIds: completionIds,
          notes: null,
          ...common,
          callback: () => snooze(),
        });
      } else if (current === 'To Do') {
        snooze();
      }
    }
  };

  onReady = () => {
    if (this.formRef && this.formRef.submit) {
      this.formRef.submit();
    }
  };

  onReset = (values) => {
    if (this.formRef && this.formRef.submit) {
      this.formRef.reset(values);
    }
  };

  componentDidMount() {
    const oga = this.props.role >= RoleTypes.OperatorGroupAdmin;

    if (!this.props.tasks.length) {
      this.props.getTasks();
    }

    if (!this.props.taskLocationList.length) {
      this.props.fetchTaskLocations();
    }

    if (!this.props.taskPrefixList.length) {
      this.props.fetchTaskPrefixes();
    }

    if (!this.props.siteOperationTypeList.length && oga) {
      this.props.fetchOperationTypes();
    }

    if (!this.props.siteModelYearList.length && oga) {
      this.props.fetchSiteModelYears();
    }
  }

  onSubmit = (value) => {
    const task = _.cloneDeep(value);
    const desiredStatus = task.projectStatus;
    delete task.projectStatus;
    const isQuick = this.props.completeQuickAddTask || !!task.quickAddTask;

    // For project quick-add, ensure required fields are populated even if fields are not mounted
    if (isQuick) {
      // Default type/interval if not set by the form
      if (task.type === undefined || task.type === null) {
        task.type = TaskType.Project;
      }
      if (task.interval === undefined || task.interval === null) {
        task.interval = Interval.Once;
      }

      // Always ensure start exists; default to today
      if (!task.start) {
        task.start = moment().startOf('day').toISOString();
      }

      // If the user selected a due date, set end to the due date
      if (task.dueDate) {
        task.end = task.dueDate;
        delete task.dueDate;
      }
    } else {
      // Non quick-add path: map dueDate to start if provided
      if (task.dueDate && !task.start) {
        task.start = task.dueDate;
        delete task.dueDate;
      }
    }
    task.videos = get(task, 'videos', []);
    task.linkedTasks = get(task, 'linkedTasks', []);

    const videoErrorCheck = [];
    if (task.videos.length) {
      _.compact(_.flatten(task.videos));
      task.videos = map(task.videos, (video) => {
        const videoCheck = checkVideoUrl(video);
        if (videoCheck === '' || videoCheck === undefined || videoCheck === 'Invalid Video URL') {
          videoErrorCheck.push(video);
        } else {
          return videoCheck;
        }
      });
    }
    if (videoErrorCheck.length) {
      map(videoErrorCheck, (video) => {
        generateToastr('error', 'Invalid Video URL:', video, { timeOut: 5000 });
      });
      return;
    }

    if (task.linkedTasks.length) {
      task.linkedTasks = _.map(task.linkedTasks, (linkedTask) => {
        return { id: linkedTask.linkedTask.value, dependency: linkedTask.dependency.value };
      });
    }

    if (task.inventory && task.inventory.length > 0) {
      task.inventory.forEach((part) => {
        part.employeeId = part.employeeId || 0;
      });
    }

    if (task.type === TaskType.Project) {
      const rawPrefix = task.prefixId;
      const numPrefix = Number(rawPrefix);
      task.prefixId =
        rawPrefix !== undefined &&
        rawPrefix !== null &&
        rawPrefix !== '' &&
        !Number.isNaN(numPrefix) &&
        numPrefix > 0
          ? numPrefix
          : null;
    } else {
      task.prefixId = null;
    }

    // Coerce locationId to number or null (API expects integer or null, not empty string)
    const rawLocationId = task.locationId;
    const numLocationId = Number(rawLocationId);
    task.locationId =
      rawLocationId !== undefined &&
      rawLocationId !== null &&
      rawLocationId !== '' &&
      !Number.isNaN(numLocationId)
        ? numLocationId
        : null;

    // Coerce car count fields to numbers for API
    const toNum = (v) => (v === '' || v == null ? null : Number(v));
    if (task.interval === Interval.CarCount) {
      task.startingCarCount = toNum(task.startingCarCount);
      task.intervalCarCount = task.repeatTask ? toNum(task.intervalCarCount) ?? 0 : 0;

      // If essential task requires operationTypeId and it's missing, default to first option
      if (this.props.isEssentialTask && !task.operationTypeId) {
        const firstOp = this.props.siteOperationTypeList && this.props.siteOperationTypeList[0];
        if (firstOp && firstOp.id) task.operationTypeId = firstOp.id;
      }
    }

    if (task.priority === undefined || task.priority === null || task.priority === '') {
      task.priority = 'Low';
    }
    // Coerce employeeId to number (or null) and map priority to numeric enum expected by backend
    if (Object.prototype.hasOwnProperty.call(task, 'employeeId')) {
      task.employeeId = task.employeeId ? Number(task.employeeId) : null;
    }
    if (Object.prototype.hasOwnProperty.call(task, 'priority')) {
      const priorityMap = { Low: 0, Medium: 1, High: 2, Urgent: 3 };
      if (typeof task.priority === 'string') {
        task.priority = priorityMap[task.priority] ?? task.priority;
      }
    }

    const intervalCarCount = task.repeatTask
      ? task.intervalCarCount == null
        ? 0
        : Number(task.intervalCarCount)
      : 0;

    // Final normalization to guarantee required fields for quick-add
    const normalizedTask = isQuick
      ? {
          name: task.name,
          type: task.type ?? TaskType.Project,
          interval: task.interval ?? Interval.Once,
          start: task.start ?? moment().startOf('day').toISOString(),
          ...task,
        }
      : task;

    const submitPayload =
      normalizedTask.interval === Interval.CarCount
        ? {
            ...normalizedTask,
            intervalCarCount,
            carCount:
              this.props.initialTask.startingCarCount !== normalizedTask.startingCarCount
                ? normalizedTask.startingCarCount
                : this.props.initialTask.carCount,
          }
        : {
            ...normalizedTask,
            intervalCarCount,
            startingCarCount: null,
          };

    const submissionPayload =
      this.props.isNew && desiredStatus && desiredStatus !== 'To Do'
        ? {
            task: submitPayload,
            meta: {
              desiredStatus,
            },
          }
        : submitPayload;

    this.props.onSubmit(submissionPayload);

    if (!this.props.isNew && desiredStatus) {
      this.applyProjectStatusChange(desiredStatus);
    }
    if (task?.addAnother) {
      this.onReset(task);
    }
  };

  render() {
    const {
      closeDialog,
      isOpen,
      initialTask,
      isNew,
      modelYears,
      totalCarCount,
      isEssentialTask,
      taskPrefixList,
      taskLocationList,
      siteOperationTypeList,
      siteModelYearList,
      employees,
      isAddingTask,
      isSavingTask,
    } = this.props;

    const enableReinitialize = !isNew;
    const disabled = checkErrors(this.props.errors);
    const submitLoading = isNew ? isAddingTask : isSavingTask;

    const taskNameLookup = (taskId) => {
      return this.props.tasks.find((t) => t.id === taskId)?.name;
    };

    const initialTaskModified = {
      ...initialTask,
      // Ensure project quick form shows date when editing
      dueDate: initialTask?.start || initialTask?.dueDate,
      quickAddTask: this.props.completeQuickAddTask ? true : initialTask?.quickAddTask,
      startingCarCount: initialTask.startingCarCount || totalCarCount,
      prefixId:
        initialTask?.prefixId !== undefined && initialTask?.prefixId !== null
          ? Number(initialTask.prefixId)
          : null,
      projectStatus: this.getProjectStatus(initialTask),
      priority: initialTask?.priority ?? 'Low',
      linkedTasks: _.map(initialTask.linkedTasks, (linkedTask) => {
        let dependency = dependencies.find((t) => t.value === linkedTask.dependency);
        return {
          linkedTask: {
            value: linkedTask.linkedTaskId,
            name: taskNameLookup(linkedTask.linkedTaskId),
          },
          dependency: { value: dependency?.id, name: dependency?.name },
        };
      }),
    };
    let taskModelYears;
    if (!isNew && modelYears && modelYears.length > 0) {
      taskModelYears = modelYears.map((modelYear) => {
        return {
          yearId: modelYear.id,
        };
      });
    }

    return (
      <TaskModalDialog
        className="enhanced-dialog"
        title={this.props.title}
        onReady={this.onReady}
        onReset={() => this.onReset(this.formRef.values())}
        isOpen={isOpen}
        isNew={isNew}
        close={closeDialog}
        disabled={disabled}
        isLoading={submitLoading}
      >
        <TaskEditForm
          className="enhanced-dialog"
          totalCarCount={totalCarCount}
          onSubmit={(values) => this.onSubmit(values)}
          ref={(r) => (this.formRef = r)}
          tasks={this.props.tasks}
          selectedTasks={this.state.selectedTasks}
          selectedDependencies={this.state.selectedDependencies}
          handleTaskSelect={this.handleTaskSelect}
          handleDependencySelect={this.handleDependencySelect}
          initialValues={initialTaskModified}
          enableReinitialize={enableReinitialize}
          hours={initialTask.hourlySchedule}
          isEssentialTask={isEssentialTask}
          taskPrefixes={taskPrefixList}
          taskLocations={taskLocationList}
          siteOperationTypes={siteOperationTypeList}
          siteModelYears={siteModelYearList}
          modelYears={taskModelYears}
          employees={employees}
          isNew={isNew}
          completeQuickAddTask={this.props.completeQuickAddTask}
        />
      </TaskModalDialog>
    );
  }
}

const getErrors = getFormSyncErrors('task');

const mapStateToProps = (state, ownProps) => {
  let initialTask = { ...initialFormValues };
  if (ownProps.taskDefaults) {
    initialTask = { ...initialTask, ...ownProps.taskDefaults };
  }
  if (!ownProps.isNew) {
    if (ownProps.location?.pathname.includes('maintenance')) {
      initialTask = selectMaintenanceTaskInEdit(state) || ownProps.initialTask;
    } else {
      initialTask = selectTaskInEdit(state) || ownProps.initialTask;
    }
    if (initialTask?.subTitle === null) {
      initialTask = { ...initialTask };
      delete initialTask.subTitle;
    }
  }

  const errors = getErrors(state);
  const isAddingTask = getApiStatus(state, 'ADD_TASK');
  const isSavingTask = getApiStatus(state, 'SAVE_TASK');
  return {
    initialTask,
    errors,
    isAddingTask,
    isSavingTask,
    tasks: selectTasks(state),
    totalCarCount: selectSiteTotalCarCount(state),
    taskPrefixList: selectTaskPrefixes(state),
    taskLocationList: selectTaskLocations(state),
    siteModelYearList: selectSiteModelYears(state),
    siteOperationTypeList: selectSiteOperationTypes(state),
    employees: selectEmployees(state),
    role: selectRole(state),
    site: Number(selectSite(state)),
    currentEmployee: selectCurrentEmployee(state),
    isSubmitting: getApiStatus(state, 'ADD_TASK') || getApiStatus(state, 'SAVE_TASK'),
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      closeDialog: hideDialog,
      triggerSubmit: submit,
      triggerReset: reset,
      getTasks: fetchTasks,
      fetchTaskPrefixes: getTaskPrefixes,
      fetchTaskLocations: getTaskLocations,
      fetchOperationTypes: getSiteOperationTypes,
      fetchSiteModelYears: getSiteModelYears,
      showDialog,
      completeTaskAOT,
      revertTaskCompletion,
      snoozeTaskCompletion,
      unsnoozeTaskCompletion,
    },
    dispatch
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(UpsertTaskDialog));
